import { PortalAdminAdvancedPortalDataPageName, PortalAdminPageName } from '@pages/PortalAdmin/PortalAdmin.constants';
import { IRoute } from '@router/routes/Route.interface';
import { PortalAdminPages } from '@pages/PortalAdmin/PortalAdmin.constants';
import { lazy } from 'react';
import { UserRoles } from '@models/user';

const BrokerageManagerPage = lazy(() => import('../../pages/PortalAdmin/BrokerageManager/BrokerageManagerPage'));
const BrokerageManagementTablePage = lazy(
  () => import('../../pages/PortalAdmin/BrokerageManager/BrokerageManagementTablePage'),
);

const GlobalAlertManagementPage = lazy(
  () => import('../../pages/PortalAdmin/GlobalAlertManagement/GlobalAlertManagementPage'),
);
const AdvancedPortalDataPage = lazy(() => import('../../pages/PortalAdmin/AdvancedPortalData/AdvancedPortalDataPage'));

const BrokerageCommissionView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/BrokerageCommissionView/BrokerageCommissionView'),
);

const BusinessClassesView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/BusinessClassesView/BusinessClassesView'),
);

const OfferingDefaultJurisdictionView = lazy(
  () =>
    import('../../pages/PortalAdmin/AdvancedPortalData/OfferingDefaultJurisdictionView/OfferingDefaultJurisdictionView'),
);

const OfferingSubjectivitiesView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/OfferingSubjectivitiesView/OfferingSubjectivitiesView'),
);

const CoverageView = lazy(() => import('../../pages/PortalAdmin/AdvancedPortalData/CoverageView/CoverageView'));

const PostRateDefaultFactorsView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/PostRateDefaultFactorsView/PostRateDefaultFactorsView'),
);

const LOIAvailabilityView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/LOIAvailabilityView/LOIAvailabilityView'),
);

const LegalNoticeManagementView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/LegalNoticeManagementView/LegalNoticeManagementView'),
);

const CustomActivityTypesView = lazy(
  () => import('../../pages/PortalAdmin/AdvancedPortalData/CustomActivityTypesView/CustomActivityTypesView'),
);
const MaintenanceModePage = lazy(() => import('../../pages/PortalAdmin/MaintenanceMode/MaintenanceModePage'));

const OfferingManagementPage = lazy(() => import('../../pages/PortalAdmin/OfferingManagement/OfferingManagementPage'));

const PortalAdminLayout = lazy(() =>
  import('../../pages/PortalAdmin/PortalAdminLayout').then((module) => ({ default: module.PortalAdminLayout })),
);

const PortalAdminHomePage = lazy(() => import('../../pages/PortalAdmin/PortalAdminHomePage'));

const QuoteSetupPresetManagementPage = lazy(
  () => import('../../pages/PortalAdmin/QuoteSetupPresetManagement/QuoteSetupPresetManagementPage'),
);

const MoratoriumManagementPage = lazy(
  () => import('../../pages/PortalAdmin/MoratoriumManagement/MoratoriumManagementPage'),
);

const UnderwriterManagementPage = lazy(
  () => import('../../pages/PortalAdmin/UnderwriterManagement/UnderwriterManagementPage'),
);

const UserManagementPage = lazy(() => import('../../pages/PortalAdmin/UserManagement/UserManagementPage'));
const FormsV2AdminPage = lazy(() => import('../../pages/PortalAdmin/FormsV2Admin/FormsV2AdminPage'));
const SubmissionDraftsPage = lazy(() => import('../../pages/PortalAdmin/SubmissionDrafts/SubmissionDraftsPage'));
const UiPlaygroundPage = lazy(() => import('../../pages/PortalAdmin/UIPlayground/UIPlaygroundPage'));
const BORTransferRequestsPage = lazy(
  () => import('../../pages/PortalAdmin/BORTransferRequests/BORTransferRequestsPage'),
);
const ApiClientManagementPage = lazy(
  () => import('../../pages/PortalAdmin/ApiClientManagement/ApiClientManagementPage'),
);
const GetAppointedPagesPage = lazy(() => import('../../pages/PortalAdmin/OnboardingPages/OnboardingPagesPage'));
const OnboardingPageEditorPage = lazy(() => import('../../pages/PortalAdmin/OnboardingPages/OnboardingPageEditorPage'));
const SalesAssignmentRulesPage = lazy(
  () => import('../../pages/PortalAdmin/SalesAssignmentRules/SalesAssignmentRulesPage'),
);
const OnboardingQueuePage = lazy(() => import('../../pages/PortalAdmin/BrokerageOnboarding/OnboardingQueuePage'));
const OnboardingWorkbenchPage = lazy(
  () => import('../../pages/PortalAdmin/BrokerageOnboarding/OnboardingWorkbenchPage'),
);
const BoxSignTemplatesPage = lazy(() => import('../../pages/PortalAdmin/BoxSignTemplates/BoxSignTemplatesPage'));
const AddBoxSignTemplatePage = lazy(() => import('../../pages/PortalAdmin/BoxSignTemplates/AddBoxSignTemplatePage'));
const BoxSignTemplateDetailPage = lazy(
  () => import('../../pages/PortalAdmin/BoxSignTemplates/BoxSignTemplateDetailPage'),
);

const PORTAL_ADMIN_ROLES = [
  UserRoles.Underwriter,
  UserRoles.LIOContractor,
  UserRoles.Producer,
  UserRoles.SrOps,
  UserRoles.SystemAdmin,
  UserRoles.PortalAdmin,
  UserRoles.LioSales,
  UserRoles.JrOps,
];

export const adminRoutes: IRoute[] = [
  {
    roles: PORTAL_ADMIN_ROLES,
    path: 'portal-admin',
    // Bare `/portal-admin` lands on the tool grid rather than dropping the user into a nested drilldown.
    indexPath: 'home',
    component: <PortalAdminLayout />,
    outlets: [
      {
        // Landing page: a role-filtered grid of every admin tool the user can access.
        component: <PortalAdminHomePage />,
        path: 'home',
        roles: PORTAL_ADMIN_ROLES,
      },
      {
        component: <BrokerageManagementTablePage />,
        path: PortalAdminPages[PortalAdminPageName.Brokerage].url,
        roles: PortalAdminPages[PortalAdminPageName.Brokerage].roles,
      },
      {
        component: <BrokerageManagerPage />,
        path: `${PortalAdminPages[PortalAdminPageName.Brokerage].url}/:brokerageId`,
        roles: PortalAdminPages[PortalAdminPageName.Brokerage].roles,
      },
      {
        component: <UserManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.User].url,
        roles: PortalAdminPages[PortalAdminPageName.User].roles,
      },
      {
        component: <OfferingManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.Offering].url,
        roles: PortalAdminPages[PortalAdminPageName.Offering].roles,
      },
      {
        component: <UnderwriterManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.UWMgmt].url,
        roles: PortalAdminPages[PortalAdminPageName.UWMgmt].roles,
      },
      {
        component: <MoratoriumManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.MoratoriumManagement].url,
        roles: PortalAdminPages[PortalAdminPageName.MoratoriumManagement].roles,
      },
      {
        component: <QuoteSetupPresetManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.QuoteSetupPresetManagement].url,
        roles: PortalAdminPages[PortalAdminPageName.QuoteSetupPresetManagement].roles,
      },
      {
        component: <AdvancedPortalDataPage />,
        path: PortalAdminPages[PortalAdminPageName.AdvancedPortalData].url,
        roles: PortalAdminPages[PortalAdminPageName.AdvancedPortalData].roles,
        indexPath: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LOIAvailability].url,
        outlets: [
          {
            component: <LOIAvailabilityView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LOIAvailability].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LOIAvailability].roles,
          },
          {
            component: <BrokerageCommissionView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BrokerageCommission].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BrokerageCommission].roles,
          },
          {
            component: <BusinessClassesView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BusinessClasses].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.BusinessClasses].roles,
          },
          {
            component: <OfferingDefaultJurisdictionView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingDefaultJurisdiction].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingDefaultJurisdiction].roles,
          },
          {
            component: <OfferingSubjectivitiesView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingSubjectivities].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.OfferingSubjectivities].roles,
          },
          {
            component: <CoverageView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.Coverages].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.Coverages].roles,
          },
          {
            component: <PostRateDefaultFactorsView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.PostRateDefaultFactors].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.PostRateDefaultFactors].roles,
          },
          {
            component: <LegalNoticeManagementView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LegalNoticeManagement].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.LegalNoticeManagement].roles,
          },
          {
            component: <CustomActivityTypesView />,
            path: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.UnderwritingActivityTypes].url,
            roles: PortalAdminPages[PortalAdminAdvancedPortalDataPageName.UnderwritingActivityTypes].roles,
          },
        ],
      },
      {
        component: <MaintenanceModePage />,
        path: PortalAdminPages[PortalAdminPageName.Maintenance].url,
        roles: PortalAdminPages[PortalAdminPageName.Maintenance].roles,
      },
      {
        component: <GlobalAlertManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.Alerts].url,
        roles: PortalAdminPages[PortalAdminPageName.Alerts].roles,
      },
      {
        component: <FormsV2AdminPage />,
        path: PortalAdminPages[PortalAdminPageName.FormsV2Admin].url,
        roles: PortalAdminPages[PortalAdminPageName.FormsV2Admin].roles,
      },
      {
        component: <SubmissionDraftsPage />,
        path: PortalAdminPages[PortalAdminPageName.SubmissionDrafts].url,
        roles: PortalAdminPages[PortalAdminPageName.SubmissionDrafts].roles,
      },
      {
        component: <UiPlaygroundPage />,
        path: PortalAdminPages[PortalAdminPageName.UiPlayground].url,
        roles: PortalAdminPages[PortalAdminPageName.UiPlayground].roles,
      },
      {
        component: <BORTransferRequestsPage />,
        path: PortalAdminPages[PortalAdminPageName.BORTransfer].url,
        roles: PortalAdminPages[PortalAdminPageName.BORTransfer].roles,
      },
      {
        component: <ApiClientManagementPage />,
        path: PortalAdminPages[PortalAdminPageName.ApiClientManagement].url,
        roles: PortalAdminPages[PortalAdminPageName.ApiClientManagement].roles,
      },
      {
        component: <GetAppointedPagesPage />,
        path: PortalAdminPages[PortalAdminPageName.GetAppointedPages].url,
        roles: PortalAdminPages[PortalAdminPageName.GetAppointedPages].roles,
      },
      {
        component: <SalesAssignmentRulesPage />,
        path: PortalAdminPages[PortalAdminPageName.SalesAssignmentRules].url,
        roles: PortalAdminPages[PortalAdminPageName.SalesAssignmentRules].roles,
      },
      {
        component: <OnboardingQueuePage />,
        path: PortalAdminPages[PortalAdminPageName.BrokerageOnboarding].url,
        roles: PortalAdminPages[PortalAdminPageName.BrokerageOnboarding].roles,
      },
      {
        component: <OnboardingWorkbenchPage />,
        path: `${PortalAdminPages[PortalAdminPageName.BrokerageOnboarding].url}/:submissionId`,
        roles: PortalAdminPages[PortalAdminPageName.BrokerageOnboarding].roles,
      },
      {
        component: <BoxSignTemplatesPage />,
        path: PortalAdminPages[PortalAdminPageName.BoxSignTemplates].url,
        roles: PortalAdminPages[PortalAdminPageName.BoxSignTemplates].roles,
      },
      {
        component: <AddBoxSignTemplatePage />,
        path: `${PortalAdminPages[PortalAdminPageName.BoxSignTemplates].url}/new`,
        roles: PortalAdminPages[PortalAdminPageName.BoxSignTemplates].roles,
      },
      {
        component: <BoxSignTemplateDetailPage />,
        path: `${PortalAdminPages[PortalAdminPageName.BoxSignTemplates].url}/:templateId`,
        roles: PortalAdminPages[PortalAdminPageName.BoxSignTemplates].roles,
      },
      {
        component: <OnboardingPageEditorPage />,
        path: `${PortalAdminPages[PortalAdminPageName.GetAppointedPages].url}/new`,
        roles: PortalAdminPages[PortalAdminPageName.GetAppointedPages].roles,
      },
      {
        component: <OnboardingPageEditorPage />,
        path: `${PortalAdminPages[PortalAdminPageName.GetAppointedPages].url}/:pageId`,
        roles: PortalAdminPages[PortalAdminPageName.GetAppointedPages].roles,
      },
    ],
  },
];
